<script>
import { useRadioSettingsStore } from '@/components/pc/radio/ipradio/stores/bcRadioSettings'
import RangeSlider from '@/components/pc/radio/ipradio/atoms/RangeSlider.vue'
import SettingGroup from '@/components/pc/radio/ipradio/organisms/layout/SettingGroup.vue'
import SettingItem from '@/components/pc/radio/ipradio/organisms/layout/SettingItem.vue'
import { BC } from '@/components/pc/radio/ipradio/config/BCconstants'

const THRESHOLD_RELATION_MARGIN = Object.freeze({
  LTE: 0,
  WIFI: 0,
})

export default {
    name: 'NetworkThresholdSettings',

    components: { RangeSlider, SettingGroup, SettingItem },

    props: {
        enableWifiOnly: {
            type: Boolean,
            default: false,
        },
    },

    computed: {
        BC() {
            return BC
        }
    },

  data() {
    const radioSettings = useRadioSettingsStore()
    return {
      radioSettings,
      form: {
        range: {
          handoverLTE: BC.NETWORK.CONFIGURATION.THRESHOLD.HANDOVER_LTE.MIN,
          handoverWiFi: BC.NETWORK.CONFIGURATION.THRESHOLD.HANDOVER_WIFI.MIN,
          reconnectionLTE: BC.NETWORK.CONFIGURATION.THRESHOLD.RECONNECTION_LTE.MIN,
          reconnectionWiFi: BC.NETWORK.CONFIGURATION.THRESHOLD.RECONNECTION_WIFI.MIN,
        },
      },
      // 初期値は暫定で MIN に。後で mounted 時に更新
      conditionalLimit: {
        upper: {
          handoverLTE: BC.NETWORK.CONFIGURATION.THRESHOLD.RECONNECTION_LTE.MIN,
          handoverWiFi: BC.NETWORK.CONFIGURATION.THRESHOLD.RECONNECTION_WIFI.MIN,
        },
        lower: {
          reconnectionLTE: BC.NETWORK.CONFIGURATION.THRESHOLD.HANDOVER_LTE.MIN,
          reconnectionWiFi: BC.NETWORK.CONFIGURATION.THRESHOLD.HANDOVER_WIFI.MIN,
        },
      },
      setting: radioSettings.getTempSetting(),
    }
  },

  watch: {
    form: {
      deep: true,
      handler() {
        this.updateConditionalLimit()
        this.transForm2Setting()
        this.radioSettings.saveTempSetting(this.setting)
      },
    },
  },

    created() {
        if (!this.enableWifiOnly) {
            this.registerThresholdWatchers('LTE')
        }
        this.registerThresholdWatchers('WIFI')
    },

    mounted() {
        this.transSetting2Form()
        if (!this.enableWifiOnly) {
            this.syncThresholdPair('LTE', 'handover')
        }
        this.syncThresholdPair('WIFI', 'handover')
        this.updateConditionalLimit()
    },

  methods: {
    getThresholdConfig(type) {
      if (type === 'LTE') {
        return {
          handoverRangeKey: 'handoverLTE',
          reconnectionRangeKey: 'reconnectionLTE',
          handoverLimit: BC.NETWORK.CONFIGURATION.THRESHOLD.HANDOVER_LTE,
          reconnectionLimit: BC.NETWORK.CONFIGURATION.THRESHOLD.RECONNECTION_LTE,
          relationMargin: THRESHOLD_RELATION_MARGIN.LTE,
        }
      }

      return {
        handoverRangeKey: 'handoverWiFi',
        reconnectionRangeKey: 'reconnectionWiFi',
        handoverLimit: BC.NETWORK.CONFIGURATION.THRESHOLD.HANDOVER_WIFI,
        reconnectionLimit: BC.NETWORK.CONFIGURATION.THRESHOLD.RECONNECTION_WIFI,
        relationMargin: THRESHOLD_RELATION_MARGIN.WIFI,
      }
    },
    getRequiredGap(type) {
      return this.getThresholdConfig(type).relationMargin + 1
    },
    clampValue(value, min, max) {
      return Math.min(max, Math.max(min, value))
    },
    syncThresholdPair(type, preferredField) {
      const {
        handoverRangeKey,
        reconnectionRangeKey,
        handoverLimit,
        reconnectionLimit,
      } = this.getThresholdConfig(type)
      const requiredGap = this.getRequiredGap(type)
      const handoverMin = handoverLimit.MIN
      const handoverMax = Math.min(handoverLimit.MAX, reconnectionLimit.MAX - requiredGap)
      const reconnectionMin = Math.max(reconnectionLimit.MIN, handoverLimit.MIN + requiredGap)
      const reconnectionMax = reconnectionLimit.MAX

      let handoverValue = this.form.range[handoverRangeKey]
      let reconnectionValue = this.form.range[reconnectionRangeKey]

      if (preferredField === 'handover') {
        handoverValue = this.clampValue(handoverValue, handoverMin, handoverMax)
        reconnectionValue = this.clampValue(
          reconnectionValue,
          Math.max(reconnectionMin, handoverValue + requiredGap),
          reconnectionMax,
        )
      } else {
        reconnectionValue = this.clampValue(reconnectionValue, reconnectionMin, reconnectionMax)
        handoverValue = this.clampValue(
          handoverValue,
          handoverMin,
          Math.min(handoverMax, reconnectionValue - requiredGap),
        )
      }

      if (this.form.range[handoverRangeKey] !== handoverValue) {
        this.form.range[handoverRangeKey] = handoverValue
      }
      if (this.form.range[reconnectionRangeKey] !== reconnectionValue) {
        this.form.range[reconnectionRangeKey] = reconnectionValue
      }
    },
    registerThresholdWatchers(type) {
      const { handoverRangeKey, reconnectionRangeKey } = this.getThresholdConfig(type)

      this.$watch(
        () => this.form.range[handoverRangeKey],
        (newVal, oldVal) => {
          if (newVal === oldVal) {
            return
          }
          this.syncThresholdPair(type, 'handover')
        },
      )

      this.$watch(
        () => this.form.range[reconnectionRangeKey],
        (newVal, oldVal) => {
          if (newVal === oldVal) {
            return
          }
          this.syncThresholdPair(type, 'reconnection')
        },
      )
    },
    transForm2Setting() {
      const data = this.setting.Network.Configuration.Threshold

      data.HandoverLTE = this.form.range.handoverLTE
      data['HandoverWi-Fi'] = this.form.range.handoverWiFi
      data.ReconnectionLTE = this.form.range.reconnectionLTE
      data['ReconnectionWi-Fi'] = this.form.range.reconnectionWiFi
    },
    transSetting2Form() {
        const data = this.setting.Network.Configuration.Threshold

        this.form.range.handoverLTE =
            data.HandoverLTE === BC.COMMON.TOGGLE.DISABLE
                ? BC.NETWORK.CONFIGURATION.THRESHOLD.HANDOVER_LTE.MIN
                : data.HandoverLTE

        this.form.range.handoverWiFi =
            data['HandoverWi-Fi'] === BC.COMMON.TOGGLE.DISABLE
                ? BC.NETWORK.CONFIGURATION.THRESHOLD.HANDOVER_WIFI.MIN
                : data['HandoverWi-Fi']

        this.form.range.reconnectionLTE = data.ReconnectionLTE
        this.form.range.reconnectionWiFi = data['ReconnectionWi-Fi']
    },
      updateConditionalLimit() {
          const lteGap = this.getRequiredGap('LTE')
          const wifiGap = this.getRequiredGap('WIFI')

          this.conditionalLimit.upper.handoverLTE = this.form.range.reconnectionLTE - lteGap
          this.conditionalLimit.upper.handoverWiFi = this.form.range.reconnectionWiFi - wifiGap
          this.conditionalLimit.lower.reconnectionLTE = this.form.range.handoverLTE + lteGap
          this.conditionalLimit.lower.reconnectionWiFi = this.form.range.handoverWiFi + wifiGap
      },
  },
}
</script>

<template>
    <div>
        <SettingGroup title="閾値設定">
            <SettingItem label="切替閾値" />
            <template v-if="!enableWifiOnly">
                <SettingItem class="noLabelSettingItem">
                    <label class="transTypeLabel">LTE</label>
                    <RangeSlider
                        :inputValue="form.range.handoverLTE"
                        @update:inputValue="form.range.handoverLTE = $event"
                        :minLimit="BC.NETWORK.CONFIGURATION.THRESHOLD.HANDOVER_LTE.MIN"
                        :maxLimit="BC.NETWORK.CONFIGURATION.THRESHOLD.HANDOVER_LTE.MAX"
                        :conditionalUpperLimit="conditionalLimit.upper.handoverLTE"
                        unit="[dB]"
                    />
                </SettingItem>
            </template>

            <SettingItem class="noLabelSettingItem">
                <label class="transTypeLabel">Wi-Fi</label>
                <RangeSlider
                    :inputValue="form.range.handoverWiFi"
                    @update:inputValue="form.range.handoverWiFi = $event"
                    :minLimit="BC.NETWORK.CONFIGURATION.THRESHOLD.HANDOVER_WIFI.MIN"
                    :maxLimit="BC.NETWORK.CONFIGURATION.THRESHOLD.HANDOVER_WIFI.MAX"
                    :conditionalUpperLimit="conditionalLimit.upper.handoverWiFi"
                    unit="[dBm]"
                />
            </SettingItem>

            <SettingItem label="復帰閾値" />
            <template v-if="!enableWifiOnly">
                <SettingItem class="noLabelSettingItem">
                    <label class="transTypeLabel">LTE</label>
                    <RangeSlider
                        :inputValue="form.range.reconnectionLTE"
                        @update:inputValue="form.range.reconnectionLTE = $event"
                        :disable="false"
                        :minLimit="BC.NETWORK.CONFIGURATION.THRESHOLD.RECONNECTION_LTE.MIN"
                        :maxLimit="BC.NETWORK.CONFIGURATION.THRESHOLD.RECONNECTION_LTE.MAX"
                        :conditionalLowerLimit="conditionalLimit.lower.reconnectionLTE"
                        unit="[dB]"
                    />
                </SettingItem>
            </template>

            <SettingItem class="noLabelSettingItem">
                <label class="transTypeLabel">Wi-Fi</label>
                <RangeSlider
                    :inputValue="form.range.reconnectionWiFi"
                    @update:inputValue="form.range.reconnectionWiFi = $event"
                    :disable="false"
                    :minLimit="BC.NETWORK.CONFIGURATION.THRESHOLD.RECONNECTION_WIFI.MIN"
                    :maxLimit="BC.NETWORK.CONFIGURATION.THRESHOLD.RECONNECTION_WIFI.MAX"
                    :conditionalLowerLimit="conditionalLimit.lower.reconnectionWiFi"
                    unit="[dBm]"
                />
            </SettingItem>
        </SettingGroup>
    </div>
</template>

<style scoped>
:deep() .el-checkbox__label {
  width: 10px;
  font-weight: normal;
}

.noLabelSettingItem /deep/ .label,
.transTypeLabel {
  width: 100px;
}
</style>
